import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import UploadFile from "@mui/icons-material/UploadFile";
import FileDownload from "@mui/icons-material/FileDownload";
import { MaterialReactTable } from "material-react-table";
import { MaterialReactTableConfig } from "components/common/table/TableConfig";
import readContractItemsImportExcel from "../../utils/importContractItemsExcel";
import validateContractItemsImport from "../../utils/validateContractItemsImport";
import contractItemsApi from "../../services/contractItems.service";

const ContractItemsImportDialog = ({
  open,
  onClose,
  contractId,
  existingItems = [],
  onImportSuccess,
  onSnackbar,
}) => {
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [generalError, setGeneralError] = useState("");
  const [targetParentItemId, setTargetParentItemId] = useState("");
  const tableConfig = useMemo(() => MaterialReactTableConfig(), []);
  const templateFileUrl = `${process.env.PUBLIC_URL || ""}/mau_import_hang_muc_hop_dong.xlsx`;

  const canImport = useMemo(
    () =>
      validationResult?.isValid &&
      validationResult?.items?.length > 0 &&
      !importing,
    [importing, validationResult],
  );

  const parentOptions = useMemo(() => {
    const flatten = (items = [], level = 0) =>
      items.flatMap((item) => {
        const normalizedItem = {
          ...item,
          level_: item.level_ ?? level,
        };
        return [
          normalizedItem,
          ...flatten(item.children || [], level + 1),
        ];
      });

    return flatten(existingItems).filter((item) => Number(item.level_ || 0) < 3);
  }, [existingItems]);

  const errorColumns = useMemo(
    () => [
      {
        accessorKey: "rowNumber",
        header: "Dòng",
        size: 70,
      },
      {
        accessorKey: "importCode",
        header: "Mã hạng mục",
        size: 100,
      },
      {
        accessorKey: "column",
        header: "Cột",
        size: 140,
      },
      {
        accessorKey: "value",
        header: "Giá trị hiện tại",
        size: 180,
        Cell: ({ cell }) => cell.getValue()?.toString?.() || "",
      },
      {
        accessorKey: "message",
        header: "Mô tả lỗi",
        size: 360,
      },
    ],
    [],
  );

  const errorRows = useMemo(
    () =>
      (validationResult?.errors || []).map((error, index) => ({
        id: `${error.rowNumber || "row"}-${index}`,
        ...error,
      })),
    [validationResult?.errors],
  );

  const errorTableConfig = useMemo(
    () => ({
      ...tableConfig,
      columns: errorColumns,
      data: errorRows,
      enableRowActions: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableGlobalFilter: false,
      enableFilters: false,
      enableHiding: false,
      enableTopToolbar: false,
      enableBottomToolbar: errorRows.length > 10,
      enablePagination: errorRows.length > 10,
      enableSorting: true,
      getRowId: (row) => row.id,
      initialState: {
        ...tableConfig.initialState,
        density: "compact",
        pagination: { pageIndex: 0, pageSize: 10 },
      },
      muiTablePaperProps: {
        ...tableConfig.muiTablePaperProps,
        sx: {
          ...tableConfig.muiTablePaperProps?.sx,
          height: "auto",
          maxHeight: 420,
          borderRadius: 1,
        },
      },
      muiTableContainerProps: {
        ...tableConfig.muiTableContainerProps,
        sx: {
          ...tableConfig.muiTableContainerProps?.sx,
          height: "auto",
          maxHeight: 420,
        },
      },
      muiTableBodyCellProps: {
        ...tableConfig.muiTableBodyCellProps,
        sx: {
          ...tableConfig.muiTableBodyCellProps?.sx,
          textAlign: "left",
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      },
    }),
    [errorColumns, errorRows, tableConfig],
  );

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setValidationResult(null);
    setGeneralError("");
  };

  const handleTargetParentChange = (event) => {
    setTargetParentItemId(event.target.value);
    setValidationResult(null);
    setGeneralError("");
  };

  const handleCheck = async () => {
    if (!file) {
      setGeneralError("Vui lòng chọn file Excel trước khi kiểm tra");
      return;
    }

    setChecking(true);
    setGeneralError("");
    setValidationResult(null);

    try {
      const rows = await readContractItemsImportExcel(file);
      const result = validateContractItemsImport({
        rows,
        existingItems,
        targetParentItemId: targetParentItemId ? Number(targetParentItemId) : null,
      });
      setValidationResult(result);
      if (result.isValid) {
        onSnackbar?.(
          "File hợp lệ. STT trong file chỉ dùng để xác định cấu trúc, hệ thống sẽ tự đánh lại STT khi import.",
          "success",
        );
      }
    } catch (error) {
      console.error("Error checking contract item import file:", error);
      setGeneralError(error.message || "Không thể đọc file Excel");
    } finally {
      setChecking(false);
    }
  };

  const handleImport = async () => {
    if (!canImport) return;

    setImporting(true);
    setGeneralError("");

    try {
      const response = await contractItemsApi.importItems(
        contractId,
        validationResult.items,
        targetParentItemId ? Number(targetParentItemId) : null,
      );
      if (response.success || response.Success) {
        onSnackbar?.("Import Excel hạng mục hợp đồng thành công", "success");
        onImportSuccess?.();
        handleClose();
      } else {
        setGeneralError(
          response.message || response.Message || "Import không thành công",
        );
      }
    } catch (error) {
      console.error("Error importing contract items:", error);
      setGeneralError(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Lỗi khi import Excel hạng mục hợp đồng",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (checking || importing) return;
    setFile(null);
    setTargetParentItemId("");
    setValidationResult(null);
    setGeneralError("");
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
        }}
      >
        Import Excel hạng mục hợp đồng
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontFamily: "Inter, sans-serif" }}
        >
          Tải file mẫu, điền dữ liệu, chọn file và kiểm tra trước khi import.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#fbfdff" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              p: 1.5,
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 0.5 }}
            >
              1. Tải file mẫu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Dùng đúng cấu trúc file mẫu để giảm lỗi khi import.
            </Typography>
            <Button
              component="a"
              href={templateFileUrl}
              download="mau_import_hang_muc_hop_dong.xlsx"
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                textTransform: "none",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Tải file mẫu
            </Button>
          </Box>

          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              p: 1.5,
              backgroundColor: "#ffffff",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 1 }}
            >
              2. Chọn file và kiểm tra
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFile />}
                disabled={checking || importing}
                sx={{
                  textTransform: "none",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Chọn file Excel
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant="contained"
                onClick={handleCheck}
                disabled={!file || checking || importing}
                sx={{
                  textTransform: "none",
                  fontFamily: "Inter, sans-serif",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Kiểm tra dữ liệu
              </Button>
            </Box>
            <Typography
              variant="body2"
              color={file ? "text.primary" : "text.secondary"}
              sx={{
                mt: 1,
                fontFamily: "Inter, sans-serif",
                wordBreak: "break-word",
              }}
            >
              {file ? file.name : "Chưa chọn file"}
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Mã hạng mục chỉ dùng để xác định cấp cha/con trong file. Khi lưu vào hệ
          thống, dữ liệu chính thức sẽ được hệ thống tự đánh lại ở cột STT.
        </Alert>

        {/* <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Import dưới hạng mục cha</InputLabel>
          <Select
            value={targetParentItemId}
            label="Import dưới hạng mục cha"
            onChange={handleTargetParentChange}
          >
            <MenuItem value="">Không chọn - import ở cấp gốc</MenuItem>
            {parentOptions.map((item) => (
              <MenuItem key={item.itemId} value={item.itemId}>
                {item.itemCode} - {item.itemName}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        {(checking || importing) && <LinearProgress sx={{ mb: 2 }} />}

        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        {validationResult?.isValid && (
          <Alert severity="success" sx={{ mb: 2 }}>
            File hợp lệ. STT trong file chỉ dùng để xác định cấu trúc, hệ thống
            sẽ tự đánh lại STT khi import. Tổng số hạng mục:{" "}
            {validationResult.items.length}
          </Alert>
        )}

        {validationResult && !validationResult.isValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            File có {validationResult.errors.length} lỗi. Vui lòng sửa file rồi
            kiểm tra lại.
          </Alert>
        )}

        {validationResult?.errors?.length > 0 && (
          <MaterialReactTable {...errorTableConfig} />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={checking || importing}
          sx={{ textTransform: "none", fontFamily: "Inter, sans-serif" }}
        >
          Hủy
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!canImport}
          sx={{
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {importing ? "Đang import..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractItemsImportDialog;
